/**
 * Display formatting.
 *
 * Quantities arrive as Decimal strings and are formatted for reading only.
 * Nothing here does arithmetic on them — the backend owns stock truth.
 */

import type { Quantity, UnitCode } from "@/api/types";

const UNIT_LABELS: Record<UnitCode, string> = {
  EA: "un",
  // Longueur. « pi » et « po » sont ce qui est écrit sur un ruban à mesurer au
  // Québec, pas « ft » et « in ».
  MM: "mm",
  CM: "cm",
  M: "m",
  IN: "po",
  FT: "pi",
  // Masse.
  MG: "mg",
  G: "g",
  KG: "kg",
  OZ: "oz",
  LB: "lb",
  // Volume.
  ML: "mL",
  L: "L",
  GAL: "gal",
  // Conditionnement, déprécié : un conditionnement est un ProductPackage.
  BOX: "boîte",
  ROLL: "rouleau",
};

/**
 * Render `"386.0000"` as `"386"` and `"37.5000"` as `"37,5"`.
 *
 * Trailing zeros come from the database's Numeric(18,4) and mean nothing to a
 * reader, but a real decimal must survive.
 */
export function formatQuantity(value: Quantity | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const [whole = "0", fraction = ""] = value.split(".");
  const trimmed = fraction.replace(/0+$/, "");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return trimmed ? `${grouped},${trimmed}` : grouped;
}

export function formatUnit(unit: UnitCode | null | undefined): string {
  if (!unit) return "";
  return UNIT_LABELS[unit] ?? unit;
}

export function formatQuantityWithUnit(
  value: Quantity | null | undefined,
  unit: UnitCode | null | undefined,
): string {
  const quantity = formatQuantity(value);
  if (quantity === "—") return quantity;
  const label = formatUnit(unit);
  return label ? `${quantity} ${label}` : quantity;
}

/** True when a Decimal string is zero, without turning it into a number. */
export function isZeroQuantity(value: Quantity | null | undefined): boolean {
  if (value === null || value === undefined || value === "") return true;
  return /^-?0*(\.0*)?$/.test(value.trim());
}

/** True when a Decimal string is negative. */
export function isNegativeQuantity(value: Quantity | null | undefined): boolean {
  return typeof value === "string" && value.trim().startsWith("-");
}

const dateTimeFormat = new Intl.DateTimeFormat("fr-CA", {
  dateStyle: "medium",
  timeStyle: "short",
});

const dateFormat = new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium" });

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : dateTimeFormat.format(date);
}

/** Une date sans heure, telle que l'API l'envoie : `2024-03-01`. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  // `new Date("2024-03-01")` est minuit **UTC**, soit la veille au Québec : une
  // fiche technique en vigueur le 1er mars s'affichait « 29 févr. ». Une date
  // sans heure n'a pas de fuseau, et se construit donc en heure locale.
  const parts = DATE_ONLY.exec(value);
  const date = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : dateFormat.format(date);
}

/** "il y a 12 min" — the activity feed reads better in relative time. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 45) return "à l'instant";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return dateFormat.format(date);
}

export function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("fr-CA").format(value);
}

const TRANSACTION_LABELS: Record<string, string> = {
  RECEIVE: "Réception",
  ISSUE: "Sortie",
  RETURN: "Retour",
  TRANSFER: "Transfert",
  ADJUSTMENT: "Ajustement",
  SCRAP: "Rebut",
  CONSUME: "Consommation",
  RESERVE: "Réservation",
  RELEASE_RESERVATION: "Libération",
  // « Retour » tout court veut déjà dire « revenu d'un chantier ». Un retour
  // fournisseur part dans l'autre sens, et confondre les deux dans l'historique
  // ferait chercher sur une tablette de la marchandise qui a quitté le bâtiment.
  SUPPLIER_RETURN: "Retour fournisseur",
};

export function formatTransactionType(value: string): string {
  return TRANSACTION_LABELS[value] ?? value;
}

const REASON_LABELS: Record<string, string> = {
  PURCHASE: "Achat",
  JOB_SITE: "Chantier",
  RETURN_FROM_JOB: "Retour de chantier",
  COUNT_CORRECTION: "Correction de comptage",
  DAMAGED: "Endommagé",
  LOST: "Perdu",
  EXPIRED: "Expiré",
  REORGANISATION: "Réorganisation",
  OPENING_BALANCE: "Solde d'ouverture",
  CORRECTION_OF_ERROR: "Correction d'erreur",
  RETURN_TO_SUPPLIER: "Retour au fournisseur",
  OTHER: "Autre",
};

export function formatReasonCode(value: string): string {
  return REASON_LABELS[value] ?? value;
}

const LOCATION_TYPE_LABELS: Record<string, string> = {
  SITE: "Site",
  WAREHOUSE: "Entrepôt",
  ZONE: "Zone",
  AISLE: "Allée",
  RACK: "Rack",
  SHELF: "Tablette",
  SLOT: "Bac",
  ROOM: "Local",
  OFFICE: "Bureau",
  WORKSHOP: "Atelier",
  VEHICLE: "Véhicule",
  JOB_SITE: "Chantier",
  CUSTOMER_SITE: "Site client",
  YARD: "Cour",
  OTHER: "Autre",
};

export function formatLocationType(value: string): string {
  return LOCATION_TYPE_LABELS[value] ?? value;
}

const CONTAINER_TYPE_LABELS: Record<string, string> = {
  PACKOUT: "Packout",
  BIN: "Bac",
  CASE: "Case",
  BOX: "Boîte",
  PALLET: "Palette",
  TOTE: "Tote",
  CHEST: "Coffre",
  CABINET: "Armoire",
  MOBILE_SHELF: "Étagère mobile",
  CART: "Chariot",
  DRAWER: "Tiroir",
  OTHER: "Autre",
};

export function formatContainerType(value: string): string {
  return CONTAINER_TYPE_LABELS[value] ?? value;
}

/** "1,2 Mo" — for a real stored size, never a made-up quota. */
export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (value < 1024) return `${value} o`;
  const units = ["ko", "Mo", "Go", "To"];
  let size = value / 1024;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  const rounded = size >= 100 ? Math.round(size) : Math.round(size * 10) / 10;
  return `${String(rounded).replace(".", ",")} ${units[index]}`;
}

/**
 * "1 produit" / "2 produits".
 *
 * French pluralises from 2, and a status bar reading "1 fichiers" is the kind of
 * detail that makes software look unfinished.
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  const word = count >= 2 ? (plural ?? `${singular}s`) : singular;
  return `${formatInteger(count)} ${word}`;
}

const ASSET_CONDITION_LABELS: Record<string, string> = {
  NEW: "Neuf",
  GOOD: "Bon",
  WORN: "Usé",
  NEEDS_REPAIR: "À réparer",
  DAMAGED: "Endommagé",
  UNKNOWN: "Inconnu",
};

export function formatAssetCondition(value: string): string {
  return ASSET_CONDITION_LABELS[value] ?? value;
}

const ASSET_LIFECYCLE_LABELS: Record<string, string> = {
  IN_SERVICE: "En service",
  IN_REPAIR: "En réparation",
  OUT_OF_SERVICE: "Hors service",
  LOST: "Perdu",
  DISCARDED: "Rebuté",
  SOLD: "Vendu",
};

export function formatAssetLifecycle(value: string): string {
  return ASSET_LIFECYCLE_LABELS[value] ?? value;
}

const OWNERSHIP_LABELS: Record<string, string> = {
  OWNED: "Propriété",
  RENTED: "Loué",
  LEASED: "En location longue durée",
  CUSTOMER_SUPPLIED: "Fourni par le client",
  EMPLOYEE_OWNED: "Appartient à l'employé",
  UNKNOWN: "Inconnu",
};

export function formatOwnership(value: string): string {
  return OWNERSHIP_LABELS[value] ?? value;
}

/**
 * An acquisition date, shown only as precisely as it is actually known.
 *
 * An inherited tool may be "1947" and nothing more; rendering that as
 * "1 janvier 1947" would invent a precision nobody has.
 */
export function formatAcquisitionDate(
  value: string | null | undefined,
  precision: string,
): string {
  if (!value) return precision === "UNKNOWN" ? "Inconnue" : "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  switch (precision) {
    case "YEAR":
      return String(date.getUTCFullYear());
    case "DECADE":
      return `Années ${Math.floor(date.getUTCFullYear() / 10) * 10}`;
    case "MONTH":
      return new Intl.DateTimeFormat("fr-CA", { year: "numeric", month: "long" }).format(date);
    case "ESTIMATED":
      return `~ ${formatDate(value)}`;
    case "UNKNOWN":
      return "Inconnue";
    default:
      return formatDate(value);
  }
}

/** Person types, in the words a French-speaking site would use. */
export function formatPersonType(value: string): string {
  switch (value) {
    case "EMPLOYEE":
      return "Employé";
    case "SUBCONTRACTOR":
      return "Sous-traitant";
    case "CUSTOMER":
      return "Client";
    case "SUPPLIER_CONTACT":
      return "Contact fournisseur";
    default:
      return "Autre";
  }
}

/**
 * How late, in words. Null when the loan is not late.
 *
 * Takes the count the server computed rather than subtracting dates here: a
 * phone with a wrong clock would otherwise disagree with the list everyone
 * else is looking at.
 */
export function formatOverdue(days: number | null | undefined): string | null {
  if (days === null || days === undefined || days <= 0) return null;
  return days === 1 ? "1 jour de retard" : `${days} jours de retard`;
}

export function formatProjectStatus(value: string): string {
  switch (value) {
    case "PLANNED":
      return "Planifié";
    case "ACTIVE":
      return "En cours";
    case "ON_HOLD":
      return "En attente";
    case "COMPLETED":
      return "Terminé";
    case "CANCELLED":
      return "Annulé";
    default:
      return value;
  }
}

/** How well a project line is covered, in the words used on site. */
export function formatRequirementStatus(value: string): string {
  switch (value) {
    case "OK":
      return "OK";
    case "PARTIAL":
      return "Partiel";
    case "SHORT":
      return "Manquant";
    default:
      return value;
  }
}

export function formatPurchaseOrderStatus(value: string): string {
  switch (value) {
    case "DRAFT":
      return "Brouillon";
    case "ORDERED":
      return "Commandé";
    case "PARTIALLY_RECEIVED":
      return "Partiellement reçu";
    case "RECEIVED":
      return "Reçu";
    case "CLOSED":
      // Volontairement pas « Reçu » : plus rien n'est attendu, et tout n'est pas
      // arrivé. Les deux mots doivent rester distincts à l'écran, sinon le
      // reliquat abandonné disparaît de l'histoire.
      return "Clôturé (reliquat abandonné)";
    case "CANCELLED":
      return "Annulé";
    default:
      return value;
  }
}

/** Pourquoi un reliquat ne viendra plus (V1.6-10A). */
export function formatRemainderReason(value: string): string {
  switch (value) {
    case "SUPPLIER_CANCELLED":
      return "Annulé par le fournisseur";
    case "DISCONTINUED":
      return "Produit discontinué";
    case "NO_LONGER_NEEDED":
      return "Plus nécessaire";
    case "DUPLICATE_ORDER":
      return "Commande en double";
    case "CUSTOMER_CHANGE":
      return "Changement du client";
    case "OTHER":
      return "Autre";
    default:
      return value;
  }
}

/** Où en est un retour fournisseur (V1.6-10A). */
export function formatSupplierReturnStatus(value: string): string {
  switch (value) {
    case "DRAFT":
      return "Brouillon";
    case "FINALIZED":
      return "Envoyé";
    default:
      return value;
  }
}

/**
 * Où en est un surplus de chantier (V1.6-10B).
 *
 * Cinq mots pour cinq états, dont deux dérivés. « Entamé » et « Réglé » ne
 * sortent d'aucune colonne : ils se lisent des décisions déjà exécutées.
 */
export function formatSurplusStatus(value: string): string {
  switch (value) {
    case "DRAFT":
      return "Brouillon";
    case "OPEN":
      return "À décider";
    case "PARTIALLY_DISPOSED":
      return "Entamé";
    case "RESOLVED":
      return "Réglé";
    case "CANCELLED":
      return "Annulé";
    default:
      return value;
  }
}

/** Ce qu'on décide de faire du surplus (V1.6-10B). */
export function formatDispositionType(value: string): string {
  switch (value) {
    case "RETURN_TO_STOCK":
      return "Retour au stock";
    case "TRANSFER_TO_PROJECT":
      return "Transfert vers un chantier";
    case "RETURN_TO_SUPPLIER":
      return "Retour fournisseur";
    case "SCRAP":
      return "Rebut";
    default:
      return value;
  }
}

/** Où en est une décision (V1.6-10B). */
export function formatDispositionStatus(value: string): string {
  switch (value) {
    case "DRAFT":
      return "À exécuter";
    case "FINALIZED":
      return "Exécutée";
    case "CANCELLED":
      return "Annulée";
    default:
      return value;
  }
}

/** Pourquoi la marchandise repart (V1.6-10A). */
export function formatSupplierReturnReason(value: string): string {
  switch (value) {
    case "DAMAGED":
      return "Endommagé";
    case "WRONG_ITEM":
      return "Mauvais article";
    case "OVER_DELIVERY":
      return "Livré en trop";
    case "DEFECTIVE":
      return "Défectueux";
    case "NOT_NEEDED":
      return "Plus nécessaire";
    case "SUPPLIER_REQUEST":
      return "Demande du fournisseur";
    case "OTHER":
      return "Autre";
    default:
      return value;
  }
}

/**
 * A money amount, or a dash.
 *
 * Never "0,00 $" for a price nobody has entered: a zero total on an order whose
 * prices are simply unknown is a figure a spreadsheet will happily add up.
 */
export function formatMoney(value: Quantity | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return "—";
  return new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 2,
  }).format(amount);
}

// --- Listes de matériel et estimations (V1.6-01) ----------------------------

export function formatMaterialListType(value: string): string {
  switch (value) {
    case "PURCHASE":
      return "Achat";
    case "PROJECT_QUOTE":
      return "Soumission";
    case "REPLENISHMENT":
      return "Réappro";
    case "PREPARATION":
      return "Préparation";
    case "GENERAL":
      return "Général";
    default:
      return value;
  }
}

export function formatMaterialListStatus(value: string): string {
  switch (value) {
    case "DRAFT":
      return "Brouillon";
    case "ACTIVE":
      return "En cours";
    case "COMPLETED":
      return "Terminée";
    case "ARCHIVED":
      return "Archivée";
    default:
      return value;
  }
}

/**
 * Four words, and none of them talks about stock.
 *
 * "Acheté" says somebody ticked the line, not that a movement was posted. The
 * ledger is the only thing that says the second, and a list never writes to it.
 */
export function formatMaterialListLineStatus(value: string): string {
  switch (value) {
    case "TO_GET":
      return "À trouver";
    case "FOUND":
      return "Trouvé";
    case "PURCHASED":
      return "Acheté";
    case "CANCELLED":
      return "Annulé";
    default:
      return value;
  }
}

export function formatPlanningPriority(value: string): string {
  switch (value) {
    case "LOW":
      return "Basse";
    case "NORMAL":
      return "Normale";
    case "HIGH":
      return "Haute";
    case "URGENT":
      return "Urgente";
    default:
      return value;
  }
}

export function formatProjectEstimateStatus(value: string): string {
  switch (value) {
    case "DRAFT":
      return "Brouillon";
    case "ACTIVE":
      return "En cours";
    case "ACCEPTED":
      return "Acceptée";
    case "REJECTED":
      return "Refusée";
    case "ARCHIVED":
      return "Archivée";
    default:
      return value;
  }
}

export function formatEstimateVersionStatus(value: string): string {
  switch (value) {
    case "DRAFT":
      return "Brouillon";
    case "FINALIZED":
      return "Figée";
    default:
      return value;
  }
}

export function formatEstimateLineType(value: string): string {
  switch (value) {
    case "MATERIAL":
      return "Matériel";
    case "LABOUR":
      return "Main-d'œuvre";
    case "EQUIPMENT":
      return "Équipement";
    case "SUBCONTRACT":
      return "Sous-traitance";
    case "OTHER":
      return "Autre";
    default:
      return value;
  }
}

/**
 * A waste factor as a percentage, or a dash.
 *
 * The backend stores a fraction — 0.10 is ten percent — and this is the one
 * place that turns it into the "10 %" a person reads. Doing the conversion in a
 * component would eventually put a 0.1 next to a 10 on two different screens.
 */
export function formatWaste(value: Quantity | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const fraction = Number(value);
  if (Number.isNaN(fraction) || fraction === 0) return "—";
  return `${new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 2 }).format(fraction * 100)} %`;
}

// --- Besoins confirmés, besoin net, suggestions (V1.6-02) -------------------

export function formatRequirementState(value: string): string {
  switch (value) {
    case "CONFIRMED":
      return "Confirmé";
    case "NEEDS_CLARIFICATION":
      return "À clarifier";
    case "EXCLUDED":
      return "Exclu";
    default:
      return value;
  }
}

export function formatRequirementOrigin(value: string): string {
  switch (value) {
    case "MANUAL":
      return "Saisi";
    case "MATERIAL_LIST":
      return "Depuis une liste";
    case "ESTIMATE":
      return "Depuis une estimation";
    case "SUGGESTION":
      return "Depuis une suggestion";
    case "IMPORT":
      return "Importé";
    default:
      return value;
  }
}

/**
 * Sur quoi une suggestion repose, dit en mots.
 *
 * Le libellé doit permettre de distinguer une donnée d'un avis sans lire le
 * détail : « relation produit » et « connaissance du modèle » ne se lisent pas
 * pareil, et c'est exactement ce qu'on veut.
 */
export function formatSuggestionBasis(value: string): string {
  switch (value) {
    case "RELATION":
      return "Relation produit";
    case "HISTORY":
      return "Chantiers passés";
    case "DOCUMENT":
      return "Document";
    case "MODEL_KNOWLEDGE":
      return "Modèle local";
    default:
      return value;
  }
}

export function formatSuggestionStatus(value: string): string {
  switch (value) {
    case "PENDING":
      return "À vérifier";
    case "ACCEPTED":
      return "Ajoutée";
    case "REJECTED":
      return "Ignorée";
    default:
      return value;
  }
}

/**
 * Un avertissement de calcul, en une phrase qu'on peut agir dessus.
 *
 * Aucun n'est une erreur : la ligne s'affiche quand même. Ils disent ce que
 * l'écran ne doit **pas** présenter comme acquis.
 */
export function formatNetWarning(value: string): string {
  switch (value) {
    case "NO_PRODUCT":
      return "Hors catalogue — rien à comparer au stock";
    case "UNIT_NOT_CONVERTIBLE":
      return "Unité incompatible avec celle du stock — calcul impossible";
    case "OVER_RESERVED":
      return "Plus réservé que possédé";
    case "NO_LOCAL_SCOPE":
      return "Chantier sans emplacement — rien n'est marqué transférable";
    case "INCOMING_LATE":
      return "Commande attendue après la date voulue";
    case "INCOMING_ETA_UNKNOWN":
      return "Commande sans date d'arrivée";
    case "SHARED":
      return "Article partagé avec d'autres besoins";
    default:
      return value;
  }
}

/** Une suggestion du modèle n'est pas un fait, et ne doit pas en avoir l'air. */
export function isModelSuggestion(basis: string): boolean {
  return basis === "MODEL_KNOWLEDGE";
}

// --- Politiques de réapprovisionnement (V1.6-03) ----------------------------

/**
 * Le mode, dit en mots — et il ne parle **que** de seuils.
 *
 * « Automatique » ne doit jamais laisser croire qu'un achat a été fait. Le
 * libellé le dit, et la carte le répète.
 */
export function formatPolicyMode(value: string): string {
  switch (value) {
    case "MANUAL":
      return "Manuel";
    case "AUTO_WITH_APPROVAL":
      return "Auto, avec approbation";
    case "AUTO":
      return "Auto (seuils seulement)";
    default:
      return value;
  }
}

export function formatRecommendationStatus(value: string): string {
  switch (value) {
    case "PENDING":
      return "À décider";
    case "APPROVED":
      return "Approuvée";
    case "REJECTED":
      return "Refusée";
    case "APPLIED":
      return "Appliquée";
    case "SUPERSEDED":
      return "Remplacée";
    default:
      return value;
  }
}

/** Pourquoi un seuil devrait bouger. Des codes, jamais une phrase libre. */
export function formatPolicyReason(value: string): string {
  switch (value) {
    case "HIGH_CONSUMPTION":
      return "Consommation en hausse";
    case "LOW_CONSUMPTION":
      return "Consommation en baisse";
    case "DEMAND_INCREASE":
      return "Demande confirmée à venir";
    case "DEMAND_DECREASE":
      return "Demande en baisse";
    case "LEAD_TIME_RISK":
      return "Délai fournisseur long";
    case "LEAD_TIME_UNKNOWN":
      return "Délai fournisseur inconnu";
    case "HIGH_VARIABILITY":
      return "Consommation irrégulière";
    case "STOCKOUT_RISK":
      return "Risque de rupture";
    case "OVERSTOCK":
      return "Surstock";
    case "DEAD_STOCK":
      return "Stock dormant";
    case "INSUFFICIENT_DATA":
      return "Pas assez d'historique";
    case "MANUAL_OVERRIDE":
      return "Saisie manuelle";
    case "REVERT":
      return "Retour arrière";
    default:
      return value;
  }
}

export function formatPolicyChangeSource(value: string): string {
  switch (value) {
    case "MANUAL":
      return "Saisi à la main";
    case "RECOMMENDATION_APPROVED":
      return "Recommandation approuvée";
    case "RECOMMENDATION_AUTO":
      return "Appliquée automatiquement";
    case "REVERT":
      return "Retour arrière";
    default:
      return value;
  }
}

/**
 * Un seuil, ou un tiret.
 *
 * Jamais « 0 » pour un seuil que personne n'a posé : un zéro se compare au
 * stock et déclenche, une absence ne déclenche rien. Les confondre ferait
 * commander à vide.
 */
export function formatThreshold(value: Quantity | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return formatQuantity(value);
}

/**
 * Le statut d'une demande de prix (V1.6-04).
 *
 * « Prête à envoyer » et pas « Envoyée » : **rien n'est parti**. Le libellé est
 * la seule chose que l'utilisateur lit, et il ne doit jamais laisser croire
 * qu'un fournisseur a reçu quoi que ce soit.
 */
export function formatRfqStatus(value: string): string {
  switch (value) {
    case "DRAFT":
      return "Brouillon";
    case "READY_TO_SEND":
      return "Prête à envoyer";
    case "CANCELLED":
      return "Annulée";
    default:
      return value;
  }
}

/** D'où vient une ligne de demande de prix. */
export function formatRfqLineSource(value: string): string {
  switch (value) {
    case "MANUAL":
      return "Saisie";
    case "PROJECT_REQUIREMENT":
      return "Besoin confirmé";
    case "MATERIAL_LIST":
      return "Liste de matériel";
    default:
      return value;
  }
}

/** Ce qu'on demande au fournisseur, en toutes lettres. */
export function formatRfqTerm(value: string): string {
  switch (value) {
    case "PRICE":
      return "Prix unitaire";
    case "AVAILABILITY":
      return "Disponibilité";
    case "LEAD_TIME":
      return "Délai";
    case "MINIMUM_ORDER":
      return "Quantité minimale";
    case "PACKAGING":
      return "Conditionnement";
    case "SHIPPING":
      return "Livraison";
    case "VALIDITY":
      return "Validité de l'offre";
    case "SUBSTITUTIONS":
      return "Équivalences";
    default:
      return value;
  }
}

/**
 * Ce qui bloque ou ce qui mérite d'être dit avant de figer.
 *
 * « Sans adresse » n'est pas formulé comme un échec d'envoi : dans V1.6-04 il
 * n'y a pas d'envoi. C'est un renseignement sur le fournisseur.
 */
export function formatRfqWarning(value: string): string {
  switch (value) {
    case "CONTACT_MISSING":
      return "Fournisseur sans adresse courriel";
    case "BASIS_STALE":
      return "Le besoin net a changé depuis la préparation";
    case "DUPLICATE_PRODUCT":
      return "Le même article apparaît deux fois";
    case "SUPPLIER_WITHOUT_LINE":
      return "Un fournisseur ne reçoit aucune ligne";
    case "LINE_WITHOUT_SUPPLIER":
      return "Une ligne n'est demandée à personne";
    case "SUPPLIER_INACTIVE":
      return "Fournisseur devenu inactif";
    default:
      return value;
  }
}

/**
 * Le statut d'un message sortant (V1.6-05).
 *
 * « Livraison incertaine » plutot que « echec » : les deux ne se traitent pas
 * pareil, et confondre les deux ferait renvoyer une demande de prix qu'un
 * fournisseur a peut-etre deja recue.
 */
export function formatOutboundStatus(value: string): string {
  switch (value) {
    case "QUEUED":
      return "En file";
    case "SENDING":
      return "Envoi en cours";
    case "SENT":
      return "Envoyé";
    case "FAILED":
      return "Échec";
    case "UNKNOWN_DELIVERY":
      return "Livraison incertaine";
    case "CANCELLED":
      return "Annulé";
    default:
      return value;
  }
}

/**
 * Où en est la transmission d'une demande de prix.
 *
 * « Jamais transmise » et pas « brouillon » : la demande peut être figée et
 * approuvée, et n'être simplement jamais partie.
 */
export function formatDispatchState(value: string): string {
  switch (value) {
    case "NOT_SENT":
      return "Jamais transmise";
    case "QUEUED":
      return "En file";
    case "SENDING":
      return "Envoi en cours";
    case "SENT":
      return "Envoyée";
    case "FAILED":
      return "Échec";
    case "UNKNOWN_DELIVERY":
      return "Livraison incertaine";
    default:
      return value;
  }
}

/** Ce qu'un message reçu a l'air d'être. Une étiquette, pas une action. */
export function formatInboundClassification(value: string): string {
  switch (value) {
    case "UNCLASSIFIED":
      return "Non classé";
    case "QUOTE":
      return "Soumission";
    case "INVOICE":
      return "Facture";
    case "CONFIRMATION":
      return "Confirmation";
    case "OTHER":
      return "Autre";
    default:
      return value;
  }
}

/** Comment un message reçu a été rattaché à une demande. */
export function formatLinkSource(value: string): string {
  switch (value) {
    case "NONE":
      return "Aucun rattachement";
    case "RFQ_CODE":
      return "Code de demande trouvé dans le message";
    case "HUMAN":
      return "Rattaché à la main";
    default:
      return value;
  }
}

/**
 * Pourquoi un envoi a échoué, en français.
 *
 * `PROVIDER_DISABLED` est le cas courant et il n'est pas une panne : c'est la
 * configuration par défaut de HD-Vault.
 */
export function formatSendError(value: string | null): string | null {
  if (!value) return null;
  switch (value) {
    case "PROVIDER_DISABLED":
      return "Envoi e-mail non configuré sur cette installation";
    case "PROVIDER_RAISED":
      return "Le service d'envoi n'a pas répondu correctement";
    case "TIMEOUT":
      return "Le service d'envoi n'a pas répondu à temps";
    default:
      return value;
  }
}


/**
 * Ou en est la transmission d'un **bon de commande** (V1.6-14.3).
 *
 * Nomme separement de `formatDispatchState`, qui traduit l'etat d'une demande
 * de prix : les deux enumerations ne partagent pas leurs valeurs — l'une dit
 * `NOT_SENT`, l'autre `NOT_QUEUED` — et les fondre ferait rendre un mot faux
 * pour l'une des deux.
 *
 * Distinct aussi de `formatPurchaseOrderStatus` : une commande peut etre
 * « Brouillon » avec un envoi « Echec ». Les confondre ferait croire qu'un bon
 * est parti alors qu'il attend encore.
 */
export function formatPurchaseOrderDispatchState(value: string): string {
  switch (value) {
    case "NOT_QUEUED":
      return "Pas encore envoyé";
    case "QUEUED":
      return "En attente d'envoi";
    case "SENDING":
      return "Envoi en cours";
    case "SENT":
      return "Envoyé";
    case "FAILED":
      return "Échec d'envoi";
    case "UNKNOWN_DELIVERY":
      return "Livraison incertaine";
    default:
      return value;
  }
}

/**
 * Ou en est la reponse du fournisseur (V1.6-14.6).
 *
 * Chaque libelle dit **ce que le fournisseur a fait**, jamais ce que HD-Vault en
 * a fait. « Refuse par le fournisseur » et non « commande annulee » : la
 * commande n'est pas annulee, et la laisser croire ferait chercher une decision
 * que personne n'a prise.
 *
 * Distinct de `formatPurchaseOrderDispatchState`, qui dit si **nous** avons
 * transmis. Les deux cohabitent sur le meme ecran et ne se confondent jamais.
 */
export function formatAcknowledgementState(value: string): string {
  switch (value) {
    case "NOT_ACKNOWLEDGED":
      return "En attente de confirmation fournisseur";
    case "CONFIRMED":
      return "Confirmé par le fournisseur";
    case "DECLINED":
      return "Refusé par le fournisseur — action requise";
    case "CHANGES_REQUESTED":
      return "Changements proposés — révision requise";
    default:
      return value;
  }
}

/** Le meme vocabulaire, au passe, pour une entree d'historique. */
export function formatAcknowledgementStatus(value: string): string {
  switch (value) {
    case "CONFIRMED":
      return "Confirmé";
    case "DECLINED":
      return "Refusé";
    case "CHANGES_REQUESTED":
      return "Changements proposés";
    default:
      return value;
  }
}

/** Par ou le fournisseur a repondu. Son canal a lui, pas notre facon de le saisir. */
export function formatAcknowledgementChannel(value: string): string {
  switch (value) {
    case "EMAIL":
      return "Courriel";
    case "PHONE":
      return "Téléphone";
    case "PORTAL":
      return "Portail fournisseur";
    case "IN_PERSON":
      return "En personne";
    case "OTHER":
      return "Autre";
    default:
      return value;
  }
}

/**
 * Pourquoi l'envoi n'est pas propose, en une phrase actionnable.
 *
 * Le serveur rend un **code** ; l'ecran le traduit. Lire une phrase humaine a
 * l'expression reguliere serait une traduction qui casse au premier mot change.
 */
export function formatDispatchBlockedReason(code: string | null): string | null {
  if (!code) return null;
  switch (code) {
    case "NO_RECIPIENT":
      return "Ce fournisseur n'a pas d'adresse courriel utilisable. Ajoutez-la sur sa fiche.";
    case "ALREADY_ORDERED":
      return "Ce bon a déjà été envoyé. Les corrections demandent un amendement, pas un renvoi.";
    case "PURCHASE_ORDER_CANCELLED":
      return "Cette commande est annulée ; elle ne s'envoie pas.";
    case "NO_LINES":
      return "Cette commande n'a aucune ligne à transmettre.";
    case "UNKNOWN_DELIVERY_NOT_RETRYABLE":
      return "La livraison est incertaine : vérifiez auprès du fournisseur avant toute action.";
    case "IN_FLIGHT":
      return "Un envoi est déjà en cours pour ce bon.";
    case "FORBIDDEN":
      return "Votre rôle ne permet pas d'envoyer un bon de commande.";
    case "COVERAGE_CHANGED":
      return "Le besoin a changé depuis la préparation de ce bon. Recalculez le plan avant de l'envoyer.";
    case "SUPPLIER_INACTIVE":
      return "Ce fournisseur n'est plus actif ; on ne lui passe pas commande.";
    default:
      return null;
  }
}
